import { useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { http, formatErr } from "@/lib/api";
import { mediaUrl } from "@/components/site/PartnerCards";

const LANGS = ["Indonesia", "English", "Melayu", "Mandarin", "Français", "Deutsch", "العربية", "日本語", "Nederlands"];

function Photos({ app, onChange }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const photos = app.profile?.photos || [];
  const upload = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post(`/admin/partner-applications/${app.id}/photos`, fd);
      onChange([...photos, data.path]);
    } catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  const remove = async (path) => {
    try { await http.delete(`/admin/partner-applications/${app.id}/photos`, { params: { path } }); onChange(photos.filter((p) => p !== path)); }
    catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {photos.map((p, i) => (
        <div key={p} className="group relative h-20 w-28 overflow-hidden rounded-xl">
          <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => remove(p)} data-testid={`partner-photo-delete-${app.id}-${i}`} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-80 transition-opacity hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      {app.profile && photos.length < 8 && (
        <label className="flex h-20 w-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-slate-300 text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-50">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}{t("pt_add_photo")}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} data-testid={`partner-photo-input-${app.id}`} className="hidden" />
        </label>
      )}
    </div>
  );
}

export default function PartnerProfileEditor({ app, onSaved }) {
  const { t } = useLang();
  const pr = app.profile || {};
  const [f, setF] = useState({ display_name: pr.display_name || app.name, bio: pr.bio || "", languages: pr.languages || [], published: !!pr.published });
  const [busy, setBusy] = useState(false);
  if (app.status !== "approved" && !app.profile) return <p className="text-xs text-slate-500">{t("pt_approve_first")}</p>;
  const toggleLang = (l) => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] });
  const save = async () => {
    setBusy(true);
    try { const { data } = await http.put(`/admin/partner-applications/${app.id}/profile`, f); onSaved({ ...app, profile: data }); toast.success(t("pt_saved")); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`partner-profile-editor-${app.id}`} className="space-y-3 rounded-2xl bg-slate-50 p-4">
      <p className="text-sm font-bold text-slate-800">{t("pt_profile_edit")}{pr.published && pr.slug && <Link to={`/mitra/${pr.slug}`} target="_blank" data-testid={`partner-profile-link-${app.id}`} className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-[#064E3B] hover:underline"><ExternalLink className="h-3 w-3" />/mitra/{pr.slug}</Link>}</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} placeholder={t("pt_display_name")} maxLength={80} data-testid={`partner-profile-name-${app.id}`} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-500" />
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid={`partner-profile-published-${app.id}`} className="h-4 w-4 accent-[#064E3B]" />{t("pt_published")}</label>
      </div>
      <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder={t("pt_bio")} maxLength={600} rows={2} data-testid={`partner-profile-bio-${app.id}`} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500" />
      <div><p className="mb-1.5 text-xs font-semibold text-slate-600">{t("pt_languages")}</p>
        <div className="flex flex-wrap gap-1.5">{LANGS.map((l) => (
          <button type="button" key={l} onClick={() => toggleLang(l)} data-testid={`partner-profile-lang-${app.id}-${l}`} className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${f.languages.includes(l) ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"}`}>{l}</button>
        ))}</div>
      </div>
      <div><p className="mb-1.5 text-xs font-semibold text-slate-600">{t("pt_fleet_photos")}</p><Photos app={app} onChange={(photos) => onSaved({ ...app, profile: { ...app.profile, photos } })} /></div>
      <button type="button" onClick={save} disabled={busy} data-testid={`partner-profile-save-${app.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#064E3B] px-4 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{t("pt_save_profile")}</button>
    </div>
  );
}
